import type { ComponentProps, ReactNode } from "react";

import { cn } from "~/lib/utils";

import { PreviousStep } from ".";

import { useState } from "react";
import { DialogTitle, DialogHeader, Modal, DialogDescription } from "~/components";

export const flowRadioClassName = "!bg-white";

export function Container({ children }: { children: ReactNode }): ReactNode {
    return <div className="flex flex-col gap-page p-page page">{children}</div>;
}

export function QuestionnaireContainer({
    className,
    children,
}: {
    className?: string;
    children: ReactNode;
}): ReactNode {
    return <section className={cn("grid lg:grid-cols-2 gap-12", className)}>{children}</section>;
}

export function QuestionInfoContainer({ className, ...props }: ComponentProps<"section">) {
    return <section className={cn("flex flex-col flex-1 justify-center gap-8", className)} {...props} />;
}

export function QuestionInfoTitle({ className, ...props }: ComponentProps<"h1">) {
    return (
        <h1
            className={cn("text-3xl md:text-4xl lg:text-5xl lg:leading-[68px] font-title text-black", className)}
            {...props}
        />
    );
}

export function QuestionInfoSubtitle({ className, ...props }: ComponentProps<"p">) {
    return <p className={cn("text-base md:text-xl md:leading-[36px] text-gray-1000", className)} {...props} />;
}

export function QuestionInfoImages(props: ComponentProps<"section">) {
    return <section className="hidden lg:grid sm:grid-cols-2 gap-4" {...props} />;
}

export function QuestionInfoImage(props: ComponentProps<"img">) {
    return <img className="shrink w-full" alt="funeral services" {...props} />;
}

export function QuestionnaireHeadingContainer({ children }) {
    return <section className="flex flex-col gap-4">{children}</section>
}

export function QuestionnaireHeading({ className, ...props }: ComponentProps<"h2">) {
    return <h2 className={cn("text-2xl sm:text-4xl font-title text-center", className)} {...props} />;
}

export function QuestionnaireSubheading({ className, ...props }: ComponentProps<"p">) {
    return <p className={cn("text-center text-base", className)} {...props} />;
}

export function QuestionContainer({ send, className, children }: ComponentProps<"div">) {
    return (
        <div className={cn("flex flex-1 flex-col gap-12 bg-muted p-4 md:p-8", className)}>
            {send ? <PreviousStep className="self-start" onClick={() => send({ type: "BACK" })} /> : null}
            <div className="flex flex-col items-center gap-10 my-auto">{children}</div>
        </div>
    );
}

export function QuestionnaireTitlesContainer({ children }: { children: ReactNode }) {
    return <div className="flex flex-col gap-4">{children}</div>;
}

export function BrooklawnAssuranceModal() {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <p>All online purchases come with the <button onClick={() => setIsOpen(true)} className="text-primary underline font-bold">Brooklawn Peace-of-Mind Purchase Assurance</button></p>
            <Modal open={isOpen} onOpenChange={setIsOpen} triggerButton={false}>
                <DialogHeader>
                    <DialogTitle>Brooklawn Peace-of-Mind Purchase Assurance</DialogTitle>
                </DialogHeader>
                <DialogDescription className="space-y-4 text-base text-foreground">
                    <p className="font-semibold">{`Here's how it works:`}</p>
                    <ol className="list-decimal list-inside space-y-3 ml-2">
                        <li className="pl-2">{`Call or come into the cemetery once you've received email confirmation of your online purchase.`}</li>
                        <li className="pl-2">{`If you don't like what you see in person, you can exchange your property for another one of equal or greater value.`}</li>
                        <li className="pl-2">{`Your original purchase price will be applied toward any new space you choose. *`}</li>
                    </ol>
                    <p>{`Our goal is to ensure you have complete peace of mind in your decision. This is our promise that you'll have the flexibility to make a choice you're truly comfortable with.`}</p>
                    <p className="font-medium">{`Peace-of-mind, online or in-person.`}</p>
                    <p className="font-medium">{`Brooklawn.`}</p>
                    <p className="text-sm text-muted-foreground italic pt-2 border-t">{`* If, for any reason, you're not fully satisfied with your purchase, you may exchange your cemetery or mausoleum property within seven (7) calendar days of your original purchase. Exchanges must be made before any interment, entombment, or memorialization takes place. No cash refunds are available under this guarantee.`}</p>
                </DialogDescription>
            </Modal>
        </>
    )
}
